import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { authService } from '../services/api';

const AuthContext = createContext(null);

/**
 * Sessão do usuário. O token fica num cookie HttpOnly (o JavaScript não o vê);
 * aqui guardamos só os dados públicos da conta retornados por /api/auth/me.
 */
export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    try {
      setUser(await authService.me());
    } catch {
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  // O interceptor do axios avisa quando a sessão expira ou a senha precisa ser trocada
  useEffect(() => {
    const onExpired = () => setUser(null);
    const onPasswordChange = () => setUser(u => (u ? { ...u, mustChangePassword: true } : u));
    window.addEventListener('auth:expired', onExpired);
    window.addEventListener('auth:password-change', onPasswordChange);
    return () => {
      window.removeEventListener('auth:expired', onExpired);
      window.removeEventListener('auth:password-change', onPasswordChange);
    };
  }, []);

  const login = useCallback(async (username, password) => {
    const me = await authService.login(username, password);
    localStorage.removeItem('currentCourseSlug');
    setUser(me);
    return me;
  }, []);

  const loginDemo = useCallback(async () => {
    const me = await authService.demoLogin();
    localStorage.removeItem('currentCourseSlug');
    setUser(me);
    return me;
  }, []);

  const logout = useCallback(async () => {
    try {
      await authService.logout();
    } finally {
      localStorage.removeItem('currentCourseSlug');
      setUser(null);
    }
  }, []);

  const changePassword = useCallback(async (currentPassword, newPassword) => {
    const me = await authService.changePassword(currentPassword, newPassword);
    setUser(me);
    return me;
  }, []);

  const value = useMemo(() => ({
    user,
    loading,
    // Admin edita conteúdo; super admin também gerencia pessoas e papéis
    isAdmin: user?.role === 'ADMIN' || user?.role === 'SUPER_ADMIN',
    isSuperAdmin: user?.role === 'SUPER_ADMIN',
    login,
    loginDemo,
    logout,
    changePassword,
    refresh,
    setUser,
  }), [user, loading, login, loginDemo, logout, changePassword, refresh]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth precisa estar dentro de AuthProvider');
  return ctx;
}
